import { useEffect, useState } from "react";
import stripeLogo from "../../../assets/stripe.png";
import { motion, AnimatePresence } from "framer-motion";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import { REST_API_URL } from "../../../api/config";
import { buildHeaders } from "../../../api/buildHeaders";
import StepLabel from "@mui/material/StepLabel";
import { useAppSelector, useAppDispatch } from "../../../hooks/redux/useRedux";
import {
  selectCheckPayment,
  selectPaymentPlans,
} from "../../../redux/slices/paymentSlice/PaymentSlice";
import { getPaymentPlansThunk } from "../../../redux/slices/paymentSlice/asyncthunk";
import { CouponField } from "./CouponField";
import { useTranslation } from "react-i18next";
import { selectUser } from "../../../redux/slices/profileSlice/ProfileSlice";
import { getUserViewThunk } from "../../../redux/slices/profileSlice/asyncthunk";

export const Subscription = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [isSwitch, setIsSwitch] = useState(false);
  const [paymentPlanIndex, setPaymentPlanIndex] = useState(0);
  const [loadingStripe, setLoadingStripe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hsActivePlan, setHsActivePlan] = useState(false);
  const [couponData, setCouponData] = useState<any | null>(null);
  const paymentCheck = useAppSelector(selectCheckPayment);
  const paymentPlans = useAppSelector(selectPaymentPlans);
  const userInfo = useAppSelector(selectUser);
  const hasSubscription =
    !!paymentCheck?.has_active_subscription ||
    !!paymentCheck?.stripe_subscription;

  const filteredByPeriod = paymentPlans.filter(
    (p: any) => p.period === (isSwitch ? "Yearly" : "Monthly"),
  );

  const currentUsage = filteredByPeriod[0]?.usage;
  const filteredPlans = filteredByPeriod.filter(
    (p: any) => p.usage === currentUsage,
  );
  const selectedPlan = filteredPlans[paymentPlanIndex] ?? null;

  // Coupon context is only relevant on monthly, non-active plans.
  const couponActive =
    (!!couponData && !isSwitch && !hsActivePlan) || userInfo?.coupon_usage;

  const discountPercent =
    couponData?.percent_off ??
    couponData?.percent ??
    couponData?.discount_percent ??
    null;
  const discountAmount =
    couponData?.amount_off ??
    couponData?.amount ??
    couponData?.discount_amount ??
    null;

  const basePrice = Number(selectedPlan?.price) || 0;
  const discountedPrice =
    discountPercent != null
      ? Math.max(0, basePrice - (basePrice * Number(discountPercent)) / 100)
      : discountAmount != null
        ? Math.max(0, basePrice - Number(discountAmount))
        : null;

  const discountLabel =
    discountPercent != null
      ? `-${discountPercent}%`
      : discountAmount != null
        ? `-$${discountAmount}`
        : null;

  const fmtPrice = (n: number) => (Number.isInteger(n) ? `${n}` : n.toFixed(2));

  const selectActivePlan = () => {
    if (!paymentCheck || !filteredPlans[paymentPlanIndex]) return;

    const isActive =
      !!paymentCheck?.stripe_subscription &&
      filteredPlans[paymentPlanIndex].stripe_price_id ===
        paymentCheck?.stripe_subscription;

    setHsActivePlan(isActive);
  };

  useEffect(() => {
    selectActivePlan();
  }, [paymentCheck, paymentPlanIndex, isSwitch]);

  const onStripe = async () => {
    try {
      setLoadingStripe(true);
      const headers = buildHeaders();
      const STRIPE_URL = `${REST_API_URL}/payment/stripe-checkout-session/`;
      const res = await fetch(STRIPE_URL, {
        method: "POST",
        body: JSON.stringify({
          stripe_price_id: selectedPlan?.stripe_price_id,
        }),
        headers,
      });
      const data = await res.json();
      window.location.href = data.url;
      setLoadingStripe(false);
    } catch (e) {
      console.log("Stripe error", e);
      setLoadingStripe(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await dispatch(getPaymentPlansThunk());
        await dispatch(getUserViewThunk());
      } catch (e) {
        console.log("Failed to init", e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  return (
    <div className="w-full max-w-full overflow-x-hidden p-4 relative self-center flex flex-col items-center h-full">
      <img
        src={stripeLogo}
        className="w-[100px] sm:w-[130px] drop-shadow-xl animate-fadeInUp relative z-10 opacity-95  "
        alt="stripe"
      />
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#020637] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <motion.div
            className="flex w-[90%] mt-4 justify-center mb-6 bg-[#EDEEF2] rounded-full p-1 relative"
            layout
          >
            <motion.div
              layout
              transition={{
                type: "spring",
                stiffness: 220,
                damping: 18,
              }}
              className="absolute top-1 bottom-1 w-1/2 rounded-full bg-[#020637]"
              style={{ left: isSwitch ? "50%" : "0%" }}
            />

            <motion.button
              whileTap={{ scale: 0.94 }}
              className={`z-10 w-1/2 py-2 text-sm font-medium transition ${
                !isSwitch ? "text-white" : "text-gray-600"
              }`}
              onClick={() => {
                setIsSwitch(false);
                setPaymentPlanIndex(0);
              }}
            >
              {t("subscription.monthly")}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.94 }}
              className={`z-10 w-1/2 py-2 text-sm font-medium transition ${
                isSwitch ? "text-white" : "text-gray-600"
              }`}
              onClick={() => {
                setIsSwitch(true);
                setPaymentPlanIndex(0);
              }}
            >
              {t("subscription.yearly")}
            </motion.button>
          </motion.div>

          <Box
            sx={{
              width: "100%",
              mt: 2,
              mb: 4,
              // On phones the stepper scrolls horizontally so many plans stay
              // legible; from sm+ it fits the available width as before.
              overflowX: { xs: "auto", sm: "visible" },
              pb: { xs: 1, sm: 0 },
            }}
          >
            <Stepper
              activeStep={paymentPlanIndex}
              alternativeLabel
              sx={{
                minWidth: {
                  xs:
                    filteredPlans.length > 3
                      ? filteredPlans.length * 96
                      : "100%",
                  sm: "100%",
                },
                ".MuiStepLabel-label": {
                  color: "#6b7280",
                  fontSize: "0.9rem",
                },
                ".Mui-active .MuiStepLabel-label": {
                  color: "#020637 !important",
                  fontWeight: 600,
                },
                ".Mui-completed .MuiStepLabel-label": {
                  color: "#020637",
                },
                ".MuiStepIcon-root": { color: "#cdd0e3" },
                ".Mui-active .MuiStepIcon-root": {
                  color: "#020637 !important",
                },
                ".Mui-completed .MuiStepIcon-root": {
                  color: "#020637 !important",
                },
              }}
            >
              {filteredPlans.map((plan, index) => (
                <Step key={plan.id}>
                  <motion.div
                    whileHover={{ scale: 1.12 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setPaymentPlanIndex(index);
                      console.log("i => ", selectedPlan);
                    }}
                    style={{ cursor: "pointer" }}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 16,
                    }}
                  >
                    <StepLabel>{plan.name}</StepLabel>
                  </motion.div>
                </Step>
              ))}
            </Stepper>
          </Box>

          {hsActivePlan && (
            <span className="flex items-center gap-2 text-green-600 font-semibold text-base mb-2">
              <span role="img" aria-label="active">
                ✅
              </span>
              {t("subscription.currentlyUsingPlan")}
            </span>
          )}

          <AnimatePresence mode="wait">
            {selectedPlan && (
              <motion.div
                key={selectedPlan.id}
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="mt-10 flex flex-col sm:flex-row gap-8 items-center"
              >
                <div className="flex-1 flex justify-center items-center min-h-[260px]">
                  <img
                    src={selectedPlan.image}
                    className="w-full h-full object-contain drop-shadow-xl"
                    alt=""
                  />
                </div>

                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-1 text-start">
                    {selectedPlan.name}
                  </h3>

                  <div className="mb-3 flex items-baseline gap-2 justify-center sm:justify-start">
                    {couponActive && discountedPrice != null ? (
                      <>
                        <span className="text-gray-400 text-xl font-semibold line-through">
                          ${selectedPlan.price}
                        </span>
                        <span className="text-[#020637] font-bold text-3xl">
                          ${fmtPrice(discountedPrice)}
                        </span>
                        {discountLabel && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">
                            {discountLabel}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-[#020637] font-bold text-3xl">
                        ${selectedPlan.price}
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 text-sm mb-5 leading-relaxed max-w-xs sm:max-w-none mx-auto sm:mx-0 text-start">
                    {selectedPlan.description}
                  </p>

                  <ul className="text-gray-700 text-sm space-y-1 mb-6">
                    {(selectedPlan.includs ?? []).map(
                      (i: { id: string; text: string }) => (
                        <li key={i.id} className="flex items-center">
                          <span className="text-[#020637] mr-2">•</span>{" "}
                          {i.text}
                        </li>
                      ),
                    )}
                  </ul>

                  {!hasSubscription && !isSwitch && !userInfo?.coupon_usage && (
                    <CouponField onApplied={setCouponData} />
                  )}

                  <motion.button
                    whileHover={hasSubscription ? undefined : { scale: 1.01 }}
                    whileTap={hasSubscription ? undefined : { scale: 0.98 }}
                    animate={
                      couponActive && !hasSubscription
                        ? {
                            boxShadow: [
                              "0 10px 25px rgba(5,150,105,0.25)",
                              "0 14px 34px rgba(5,150,105,0.45)",
                              "0 10px 25px rgba(5,150,105,0.25)",
                            ],
                          }
                        : { boxShadow: "0 6px 16px rgba(2,6,55,0.18)" }
                    }
                    transition={{
                      duration: 2,
                      repeat: couponActive && !hasSubscription ? Infinity : 0,
                      ease: "easeInOut",
                    }}
                    className={`relative w-full overflow-hidden rounded-xl px-10 py-3 font-medium transition
                      ${
                        hsActivePlan
                          ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                          : couponActive && !hasSubscription
                            ? "bg-gradient-to-r from-[#020637] via-[#0e3a7a] to-emerald-600 text-white cursor-pointer"
                            : "bg-[#020637] hover:bg-[#0e1c7a] text-white cursor-pointer"
                      }
                    `}
                    onClick={onStripe}
                    disabled={hsActivePlan || loadingStripe}
                  >
                    {loadingStripe ? (
                      t("subscription.loading")
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        {couponActive && !hasSubscription && (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                        {t("subscription.continue")}
                        {couponActive && !hasSubscription && (
                          <span className="ml-1 rounded-full bg-white/20 px-2 py-0.5 text-sm font-bold backdrop-blur-sm">
                            {discountedPrice != null
                              ? `$${fmtPrice(discountedPrice)}${
                                  discountLabel ? ` · ${discountLabel}` : ""
                                }`
                              : t("subscription.couponApplied")}
                          </span>
                        )}
                      </span>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
};
